import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Puts a new page at its beginning.
 *
 * Changing route client-side leaves the scroll position alone, so following a
 * link from the foot of a long page — the "vous ne trouvez pas ?" band at the
 * bottom of the directory, say — landed the reader in the middle of the feed
 * with no idea they were at the top of nothing.
 *
 * Three things it deliberately does not do:
 *
 * - It watches the PATH, not the whole location, and compares it against the
 *   previous one rather than trusting the effect to fire only on a real
 *   change. The directory and the feed keep their filters in the query
 *   string: typing in the search box rewrites the URL, which flips the
 *   navigation type to REPLACE and re-runs this effect with the same path —
 *   enough, in a first attempt at this, to yank the reader back to the top on
 *   every keystroke.
 * - It leaves a link with a fragment alone: that link named where it wanted
 *   to go.
 * - It leaves the Back button alone. Returning to a list you had scrolled
 *   through should not lose your place, and scrolling to the top would be a
 *   different bug in the same family.
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();
  const previousPath = useRef(pathname);

  useEffect(() => {
    const changedPage = pathname !== previousPath.current;

    previousPath.current = pathname;

    if (!changedPage || hash || 'POP' === navigationType) {
      return;
    }

    window.scrollTo(0, 0);
  }, [pathname, hash, navigationType]);

  return null;
}

export default ScrollToTop;
